import { ActionInjection } from "app/actions/di/ActionInjection";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { isObject, isUndefined } from "lodash";
import { injectable } from "tsyringe";
import { FeatureFormat } from "utils/format/FeatureFormat";
import { avoidDuplicate } from "utils/helpersInTs/helpersInTs";
import { ActionRequestEntity } from "../entites/ActionRequestEntity";
import { ArrayEditActionEntity } from "../entites/ArrayEditActionEntity";
import { IActionRepository } from "../repositories/IActionRepository";

@injectable()
export class ActionUseCase {
  private actionRepo: IActionRepository;
  
  constructor() {
    this.actionRepo = ActionInjection.getRepo();
  }

  private canAccess(path: string) {
    return FeatureFormat.findFeature(path);
  }

  public buildArrayEditActions(
    bodyVal: Array<string>,
    comparedVal:
      | Array<BaseFilterListResponseModel>
      | Array<BaseUserResponseModel>,
  ): ArrayEditActionEntity | undefined {
    const selectedVal = (Array.isArray(bodyVal) ? bodyVal : []).filter(
      Boolean,
    ) as string[];
    const currentVal =
      comparedVal?.map((item) => item?.code).filter(Boolean) || [];

    const add = selectedVal.filter((item) => !currentVal.includes(item));
    const erase = currentVal.filter((item) => !selectedVal.includes(item));

    if (!add.length && !erase.length) {
      return undefined;
    }

    return {
      add: add.length ? add : undefined,
      erase: erase.length ? erase : undefined,
    };
  }

  private adjustBody<Body extends Object, OriginInfo extends Object>(
    body: Body,
    originInfo: OriginInfo,
    keysToAvoid: string[] = [], //Là danh sách key cần định danh đặc biệt hoặc không vào luồng check trùng
    allValToString: boolean = false,
  ) {
    const renderedBody = Object.keys(body)?.filter(
      (item) =>
        item !== "code" &&
        !Array.isArray(body[item]) &&
        !keysToAvoid?.includes(item) &&
        !isUndefined(
          avoidDuplicate(
            isObject(originInfo[item])
              ? originInfo[item]?.["code"]
              : originInfo[item]?.toString(),
            body[item]?.toString(),
          ),
        ),
    );
    const leftOverBody = Object?.keys(body)?.filter((key) =>
      keysToAvoid?.includes(key),
    );
    const afterBody = {
      code: body["code"],
      ...renderedBody?.reduce(
        (a, v) => ({
          ...a,
          [v]: allValToString === true ? body[v]?.toString() : body[v],
        }),
        {},
      ),
      ...leftOverBody?.reduce(
        (a, v) => ({
          ...a,
          [v]: body[v],
        }),
        {},
      ),
    };

    return afterBody;
  }

  public async getDetail<Params extends Object>(
    request: ActionRequestEntity<Params>,
    onCallback?: Promise<BaseApiResponseModel<Object>>,
  ) {
    if (!this.canAccess(request.path)) {
      return;
    }
    return (
      (await onCallback) ||
      (await this.actionRepo.getDetail(request?.path, request?.inputValue))
    );
  }

  public async update<
    Body extends Object,
    Detail extends Object,
    Response extends Object,
  >(
    request: ActionRequestEntity<Body, Detail>,
    onCallback?: Promise<BaseApiResponseModel<Response>>,
  ): Promise<BaseApiResponseModel<Response> | undefined> {
    if (!this.canAccess(request.path)) {
      return;
    }
    const updatedBody = this.adjustBody(
      request.inputValue ?? ({} as Body),
      request.comparedValue ?? ({} as Detail),
      request.keysToAvoid,
      request.allValToString || false,
    );
    return (
      (await onCallback) ||
      (await this.actionRepo.update(request.path, updatedBody))
    );
  }

  public async create<Body extends Object, Response extends Object>(
    request: ActionRequestEntity<Body>,
    onCallback?: Promise<BaseApiResponseModel<Response>>,
  ): Promise<BaseApiResponseModel<Response> | undefined> {
    if (!this.canAccess(request.path)) {
      return;
    }
    return (
      (await onCallback) ||
      (await this.actionRepo.create(request.path, request.inputValue as Body))
    );
  }

  public async delete<Params extends Object, Response extends Object>(
    request: ActionRequestEntity<Params>,
    onCallback?: Promise<BaseApiResponseModel<Response>>,
  ): Promise<BaseApiResponseModel<Response> | undefined> {
    if (!this.canAccess(request.path)) {
      return;
    }
    return (
      (await onCallback) ||
      (await this.actionRepo.delete(request.path, request.inputValue as Params))
    );
  }

  public async export<Params extends Object>(
    request: ActionRequestEntity<Params>,
    onCallback?: Promise<BaseApiResponseModel<Blob>>,
  ): Promise<BaseApiResponseModel<Blob> | undefined> {
    if (!this.canAccess(request.path)) {
      return;
    }
    return (
      (await onCallback) ||
      (await this.actionRepo.export(request.path, request.inputValue as Params))
    );
  }

  public async combineWithFormData<Response extends Object>(
    path: string,
    body: FormData,
    onCallback?: Promise<BaseApiResponseModel<Response>>,
  ): Promise<BaseApiResponseModel<Response> | undefined> {
    if (!this.canAccess(path)) {
      return;
    }
    return (
      (await onCallback) ||
      (await this.actionRepo.combineWithFormData(path, body))
    );
  }
}
